import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { trackToolAction } from '../utils/metrics';
import CaseOverviewMap from '../components/CaseOverviewMap';
import EventEditorDialog from '../components/EventEditorDialog';
import { EVENT_CATEGORIES, categoryInfo } from '../utils/timelineCategories';
import {
  listTimelines, createTimeline, getTimeline, updateTimelineMeta, deleteTimeline,
  listEvents, saveEvent, deleteEvent, exportTimelineJson, importTimelineFromJson,
} from '../utils/timelineDb';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

const ACCENT = '#6d28d9';
const ACCENT_HOVER = '#5b21b6';

const safeFileName = s =>
  (s || 'case-timeline').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'case-timeline';

function fmtEventDate(ev) {
  if (ev.dateLabel) return ev.dateLabel;
  if (!ev.date) return '';
  if (ev.dateType === 'exact') {
    const d = new Date(ev.date);
    return isNaN(d) ? ev.date : d.toLocaleString();
  }
  const d = new Date(`${ev.date}T00:00:00`);
  return isNaN(d) ? ev.date : d.toLocaleDateString();
}

// ─── PDF export ─────────────────────────────────────────────────────────────
// Runs 100% client-side, same OSM-tile-to-canvas approach already used by
// Video Canvass Tracker / Area Search Coordinator's exporters.

const TILE = 256;
const lngToWorldX = (lng, z) => ((lng + 180) / 360) * TILE * 2 ** z;
const latToWorldY = (lat, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

// Tile images are cached across the whole export pass (shared `cache` map) so
// the overview map and per-event mini-maps don't re-fetch overlapping tiles.
function loadTileImageCached(url, cache) {
  if (cache.has(url)) return cache.get(url);
  const p = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
  cache.set(url, p);
  return p;
}

async function renderTileMapImage({ points, width, height, cache }) {
  const pts = points.filter(p => p.lat != null && p.lng != null);
  if (!pts.length) return null;

  const lats = pts.map(p => +p.lat), lngs = pts.map(p => +p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);

  const pad = 1.3;
  let zoom = 1;
  for (let z = 18; z >= 1; z--) {
    const w = (lngToWorldX(maxLng, z) - lngToWorldX(minLng, z)) * pad;
    const h = (latToWorldY(minLat, z) - latToWorldY(maxLat, z)) * pad;
    if (w <= width && h <= height) { zoom = z; break; }
  }
  if (minLat === maxLat && minLng === maxLng) zoom = 16;

  const centerX = (lngToWorldX(minLng, zoom) + lngToWorldX(maxLng, zoom)) / 2;
  const centerY = (latToWorldY(minLat, zoom) + latToWorldY(maxLat, zoom)) / 2;
  const originX = centerX - width / 2;
  const originY = centerY - height / 2;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e5e7eb';
  ctx.fillRect(0, 0, width, height);

  const n = 2 ** zoom;
  const subs = ['a', 'b', 'c'];
  const x0 = Math.floor(originX / TILE), x1 = Math.floor((originX + width) / TILE);
  const y0 = Math.floor(originY / TILE), y1 = Math.floor((originY + height) / TILE);
  const jobs = [];
  for (let tx = x0; tx <= x1; tx++) {
    for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      const s = subs[Math.abs(tx + ty) % subs.length];
      const url = `https://${s}.tile.openstreetmap.org/${zoom}/${wx}/${ty}.png`;
      const dx = tx * TILE - originX, dy = ty * TILE - originY;
      jobs.push(loadTileImageCached(url, cache).then(img => ctx.drawImage(img, dx, dy)).catch(() => {}));
    }
  }
  await Promise.all(jobs);

  for (const p of pts) {
    const px = lngToWorldX(+p.lng, zoom) - originX;
    const py = latToWorldY(+p.lat, zoom) - originY;
    const color = p.color || '#0f172a';
    ctx.beginPath();
    ctx.arc(px, py, 7, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(px, py, 8.5, 0, 2 * Math.PI);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
  }

  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

async function exportCaseTimelinePdf(timeline, events) {
  const { default: pdfMake } = await import('pdfmake/build/pdfmake');
  const pdfFonts = await import('pdfmake/build/vfs_fonts');
  pdfMake.vfs = pdfFonts.default || pdfFonts;

  const tileCache = new Map();
  const geoEvents = events.filter(e => e.location);

  const overviewMapImage = await renderTileMapImage({
    points: geoEvents.map(e => ({ lat: e.location.lat, lng: e.location.lng, color: categoryInfo(e.category).color })),
    width: 1000, height: 460, cache: tileCache,
  }).catch(() => null);

  const miniMaps = {};
  await Promise.all(geoEvents.map(async e => {
    miniMaps[e.id] = await renderTileMapImage({
      points: [{ lat: e.location.lat, lng: e.location.lng, color: categoryInfo(e.category).color }],
      width: 340, height: 240, cache: tileCache,
    }).catch(() => null);
  }));

  const usedCategories = [...new Set(events.map(e => e.category))].map(categoryInfo);
  const legendRow = usedCategories.length ? {
    columns: usedCategories.flatMap(c => ([
      { width: 12, canvas: [{ type: 'rect', x: 1, y: 2, w: 10, h: 10, r: 2, color: c.color }] },
      { width: 'auto', text: c.label, fontSize: 9, color: '#334155', margin: [4, 0, 14, 0] },
    ])),
    margin: [0, 6, 0, 14],
  } : null;

  const dateRangeText = timeline.dateRange && timeline.dateRange.start
    ? `${new Date(timeline.dateRange.start).toLocaleDateString()} – ${new Date(timeline.dateRange.end).toLocaleDateString()}`
    : 'no dated events yet';

  const sortedEvents = [...events].sort((a, b) => a.sortTimestamp - b.sortTimestamp);
  const eventCards = sortedEvents.map(ev => {
    const cat = categoryInfo(ev.category);
    const contentStack = [
      {
        columns: [
          { text: fmtEventDate(ev), bold: true, fontSize: 10, color: '#0f172a', width: 'auto' },
          {
            width: 'auto',
            table: { widths: [11, 'auto'], body: [[
              { canvas: [{ type: 'rect', x: 1, y: 2, w: 9, h: 9, r: 2, color: cat.color }] },
              { text: cat.label, fontSize: 8.5, color: '#475569', noWrap: true },
            ]] },
            layout: 'noBorders',
          },
        ],
        columnGap: 10,
      },
      { text: ev.title, fontSize: 12, bold: true, color: '#0f172a', margin: [0, 3, 0, 2] },
    ];
    if (ev.notes) contentStack.push({ text: ev.notes, fontSize: 9.5, color: '#334155', margin: [0, 0, 0, 3] });
    if (ev.source) contentStack.push({ text: `Source: ${ev.source}`, fontSize: 8.5, italics: true, color: '#64748b', margin: [0, 0, 0, 3] });
    if (ev.attachment) contentStack.push({ text: `Attached: ${ev.attachment.fileName}`, fontSize: 8.5, color: '#64748b', margin: [0, 0, 0, 3] });

    const media = [];
    if (ev.image) media.push({ image: ev.image.dataUrl, width: 170, height: 120, fit: [170, 120] });
    if (ev.location && miniMaps[ev.id]) media.push({ image: miniMaps[ev.id], width: 170, height: 120 });
    if (media.length) contentStack.push({ columns: media, columnGap: 10, margin: [0, 4, 0, 0] });

    return {
      table: {
        widths: [6, '*'],
        body: [[
          { text: '', fillColor: cat.color },
          { stack: contentStack, margin: [10, 8, 8, 8] },
        ]],
      },
      layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
      margin: [0, 0, 0, 12],
      unbreakable: true,
    };
  });

  const docDefinition = {
    pageOrientation: 'landscape',
    pageMargins: [28, 36, 28, 36],
    defaultStyle: { fontSize: 9 },
    content: [
      { text: timeline.title || 'Case Timeline', fontSize: 20, bold: true, margin: [0, 0, 0, 2] },
      timeline.description ? { text: timeline.description, color: '#475569', margin: [0, 0, 0, 4] } : null,
      { text: `${timeline.caseNumber ? `Case ${timeline.caseNumber} · ` : ''}${events.length} event${events.length !== 1 ? 's' : ''} · ${dateRangeText} · Generated ${new Date().toLocaleString()}`,
        color: '#64748b', fontSize: 8, margin: [0, 0, 0, 10] },
      overviewMapImage
        ? { image: overviewMapImage, width: 785, margin: [0, 0, 0, 4] }
        : { text: 'Overview map unavailable (no geolocated events, or map tiles could not be loaded).', italics: true, color: '#94a3b8', margin: [0, 0, 0, 10] },
      legendRow,
      { text: 'Chronological Timeline', fontSize: 14, bold: true, color: '#0f172a', margin: [0, 4, 0, 10] },
      events.length === 0 ? { text: 'No events recorded yet.', italics: true, color: '#94a3b8' } : null,
      ...eventCards,
    ].filter(Boolean),
  };

  pdfMake.createPdf(docDefinition).download(`${safeFileName(timeline.title)}.pdf`);
}

// ─── Landing view ───────────────────────────────────────────────────────────

function CaseTimelineLanding() {
  const navigate = useNavigate();
  const [timelines, setTimelines] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCaseNumber, setNewCaseNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const importInputRef = useRef(null);

  const refresh = useCallback(() => { listTimelines().then(setTimelines).catch(() => setTimelines([])); }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function create() {
    if (busy) return;
    setBusy(true);
    try {
      const t = await createTimeline({ title: newTitle, description: newDesc, caseNumber: newCaseNumber });
      navigate(`/timeline/${t.id}`);
    } catch (e) {
      setBusy(false);
      alert(`Could not create the timeline: ${e.message}`);
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete "${t.title}" and all of its events? This cannot be undone.`)) return;
    await deleteTimeline(t.id);
    refresh();
  }

  async function handleImport(file) {
    if (!file) return;
    setBusy(true);
    try {
      const id = await importTimelineFromJson(file);
      navigate(`/timeline/${id}`);
    } catch (e) {
      setBusy(false);
      alert(`Could not import: ${e.message}`);
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
          Case Timeline Builder
        </Typography>
        <Typography sx={{ color: '#64748b', mt: '6px', fontSize: '15px' }}>
          Assemble phone records, screenshots, interviews, reports, and anything else into one
          chronological timeline — with a map of where each piece happened — and export a
          polished PDF. Everything stays on this device.
        </Typography>
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.25,
        bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#334155',
        borderRadius: '10px', p: '12px 16px', mb: 2.5, fontSize: '13px', lineHeight: 1.6,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ flexShrink: 0, marginTop: '1px', color: '#64748b' }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <span>
          <strong>How storage works:</strong> each timeline is saved only in this browser, on this device — not in
          the cloud, and not synced anywhere. Nothing here ever leaves your device except when you explicitly export
          it. Use <strong>Export JSON</strong> to save a complete, portable backup (including photos and attached
          files) that you can re-import later or move to another computer with <strong>Import JSON</strong>. Use{' '}
          <strong>Export PDF</strong> when you want a polished report to view, print, or share — that&rsquo;s a
          one-way snapshot for reading, not something you can import back in.
        </span>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '24px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Your timelines</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Load a timeline previously saved with Export JSON — from this device or one you moved it from.">
              <span>
                <Button size="small" variant="outlined" onClick={() => importInputRef.current?.click()} disabled={busy}
                  sx={{ borderColor: ACCENT, color: ACCENT, fontWeight: 600 }}>
                  Import JSON
                </Button>
              </span>
            </Tooltip>
            <Button size="small" variant="contained" onClick={() => setNewOpen(true)}
              sx={{ bgcolor: ACCENT, fontWeight: 600, '&:hover': { bgcolor: ACCENT_HOVER } }}>
              + New Timeline
            </Button>
          </Box>
          <input ref={importInputRef} type="file" accept="application/json,.json" style={{ display: 'none' }}
            onChange={e => { handleImport(e.target.files && e.target.files[0]); e.target.value = ''; }} />
        </Box>

        {timelines === null && <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>Loading…</Typography>}
        {timelines && timelines.length === 0 && (
          <Typography sx={{ color: '#94a3b8', fontSize: '13px' }}>No timelines yet — start a new one above.</Typography>
        )}
        {timelines && timelines.map(t => (
          <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '12px 4px', borderTop: '1px solid #f1f5f9' }}>
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/timeline/${t.id}`)}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{t.title}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '2px' }}>
                {t.eventCount || 0} event{t.eventCount === 1 ? '' : 's'}
                {t.dateRange && t.dateRange.start ? ` · ${new Date(t.dateRange.start).toLocaleDateString()} – ${new Date(t.dateRange.end).toLocaleDateString()}` : ''}
                {' · updated '}{new Date(t.updatedAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Button size="small" onClick={() => navigate(`/timeline/${t.id}`)} sx={{ color: ACCENT, fontWeight: 600 }}>Open</Button>
            <Button size="small" onClick={() => remove(t)} sx={{ color: '#dc2626' }}>Delete</Button>
          </Box>
        ))}
      </Paper>

      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New timeline</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField label="Case title" value={newTitle} onChange={e => setNewTitle(e.target.value)} size="small" fullWidth autoFocus />
            <TextField label="Case number (optional)" value={newCaseNumber} onChange={e => setNewCaseNumber(e.target.value)} size="small" fullWidth />
            <TextField label="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} size="small" fullWidth multiline minRows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '12px 20px' }}>
          <Button onClick={() => setNewOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={create} variant="contained" disabled={busy} sx={{ bgcolor: ACCENT, fontWeight: 600, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

// ─── Editor view ────────────────────────────────────────────────────────────

function CaseTimelineEditor({ timelineId }) {
  const [timeline, setTimeline] = useState(undefined); // undefined=loading, null=not found
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [tab, setTab] = useState('list');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(0);
  const savedTimerRef = useRef(null);
  const metaTimerRef = useRef(null);

  const refreshAll = useCallback(async () => {
    const [tl, ev] = await Promise.all([getTimeline(timelineId), listEvents(timelineId)]);
    setTimeline(tl || null);
    setEvents(ev);
  }, [timelineId]);

  useEffect(() => { refreshAll(); }, [refreshAll]);
  useEffect(() => () => { if (savedTimerRef.current) clearTimeout(savedTimerRef.current); if (metaTimerRef.current) clearTimeout(metaTimerRef.current); }, []);

  function flashSaved() {
    setSaving(false);
    setSavedTick(t => t + 1);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedTick(0), 1800);
  }

  async function handleSaveEvent(draft) {
    trackToolAction('/timeline', 'Case Timeline Builder');
    setSaving(true);
    try {
      await saveEvent({ ...draft, timelineId });
      await refreshAll();
      setDialogOpen(false);
      flashSaved();
    } catch (e) {
      setSaving(false);
      alert(`Could not save: ${e.message}`);
    }
  }

  async function handleDeleteEvent(id) {
    if (!window.confirm('Delete this event?')) return;
    setSaving(true);
    try {
      await deleteEvent(id, timelineId);
      await refreshAll();
      setDialogOpen(false);
      flashSaved();
    } catch (e) {
      setSaving(false);
      alert(`Could not delete: ${e.message}`);
    }
  }

  function updateMeta(patch) {
    setTimeline(t => ({ ...t, ...patch }));
    if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    setSaving(true);
    metaTimerRef.current = setTimeout(async () => {
      try { await updateTimelineMeta(timelineId, patch); flashSaved(); }
      catch { setSaving(false); }
    }, 600);
  }

  function openNew() { setEditingEvent(null); setDialogOpen(true); }
  function openEdit(ev) { setEditingEvent(ev); setDialogOpen(true); }

  const visibleEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter(e => {
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (!q) return true;
      const hay = `${e.title || ''} ${e.notes || ''} ${e.source || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, search, categoryFilter]);

  const mapPoints = useMemo(() => events.filter(e => e.location).map(e => ({
    id: e.id, lat: e.location.lat, lng: e.location.lng, color: categoryInfo(e.category).color, label: e.title,
  })), [events]);

  async function runExportPdf() {
    setExporting(true);
    try {
      await exportCaseTimelinePdf(timeline, events);
    } catch (e) {
      alert(`Could not export: ${e.message}`);
    } finally {
      setExporting(false);
    }
  }

  async function runExportJson() {
    try {
      await exportTimelineJson(timelineId);
    } catch (e) {
      alert(`Could not export: ${e.message}`);
    }
  }

  if (timeline === undefined) {
    return <Container maxWidth="md" sx={{ py: 6 }}><Typography sx={{ color: '#64748b' }}>Loading timeline…</Typography></Container>;
  }
  if (timeline === null) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ borderRadius: '14px', p: '28px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,.07)' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 1 }}>Timeline not found</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '14px', mb: 2 }}>
            This timeline may have been deleted, or you&rsquo;re on a different device or browser than where it
            was created — timelines are stored only on the device that made them.
          </Typography>
          <Button component={Link} to="/timeline" variant="contained" sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            Back to your timelines
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <TextField variant="standard" value={timeline.title} onChange={e => updateMeta({ title: e.target.value })}
            InputProps={{ disableUnderline: true, style: { fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' } }}
            fullWidth />
          <TextField variant="standard" placeholder="Add a description…" value={timeline.description || ''} onChange={e => updateMeta({ description: e.target.value })}
            InputProps={{ disableUnderline: true, style: { fontSize: 14, color: '#64748b' } }} fullWidth />
          <Typography sx={{ color: '#94a3b8', mt: '6px', fontSize: '13px' }}>
            {events.length} event{events.length !== 1 ? 's' : ''}
            {timeline.caseNumber ? ` · Case ${timeline.caseNumber}` : ''}
            {saving ? ' · Saving…' : savedTick ? ' · Saved ✓' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Save a complete, portable backup of this timeline — including photos and attached files — that you can re-import later or move to another computer.">
            <Button onClick={runExportJson} variant="outlined" sx={{ borderColor: '#cbd5e1', color: '#334155', fontWeight: 600 }}>
              Export JSON
            </Button>
          </Tooltip>
          <Tooltip title="Generate a polished PDF report to view, print, or share. This is a one-way snapshot — it can't be re-imported.">
            <span>
              <Button onClick={runExportPdf} variant="outlined" disabled={exporting}
                sx={{ borderColor: ACCENT, color: ACCENT, fontWeight: 600, '&:hover': { borderColor: ACCENT_HOVER, bgcolor: '#f5f3ff' } }}>
                {exporting ? 'Exporting…' : 'Export PDF'}
              </Button>
            </span>
          </Tooltip>
          <Button onClick={openNew} variant="contained" sx={{ bgcolor: ACCENT, fontWeight: 600, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            + Add Event
          </Button>
        </Box>
      </Box>

      <Typography sx={{ fontSize: '12px', color: '#94a3b8', mb: 2 }}>
        Stored only on this browser, on this device — not the cloud, not synced anywhere. Use{' '}
        <strong>Export JSON</strong> regularly to back up your work (photos and attachments included) —
        browser storage can be cleared and isn&rsquo;t a substitute for a real save file.
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.05)', p: '12px', mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: 36, mb: 1 }} textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: ACCENT } }}>
          <Tab value="list" label="List" sx={{ minHeight: 36, fontWeight: 600, textTransform: 'none' }} />
          <Tab value="map" label="Map" sx={{ minHeight: 36, fontWeight: 600, textTransform: 'none' }} />
        </Tabs>

        {tab === 'list' ? (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, px: '4px' }}>
              <TextField size="small" placeholder="Search title, notes, source…" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: '1 1 220px' }} />
              <TextField select size="small" label="Category" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="all">All categories</MenuItem>
                {EVENT_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Box>
            {events.length === 0 ? (
              <Typography sx={{ color: '#94a3b8', fontSize: '13px', px: '4px' }}>No events yet — add the first one above.</Typography>
            ) : visibleEvents.length === 0 ? (
              <Typography sx={{ color: '#94a3b8', fontSize: '13px', px: '4px' }}>No events match your search/filter.</Typography>
            ) : visibleEvents.map(ev => {
              const cat = categoryInfo(ev.category);
              return (
                <Box key={ev.id} onClick={() => openEdit(ev)}
                  sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: '10px 8px', borderTop: '1px solid #f1f5f9', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}>
                  {ev.image && (
                    <Box component="img" src={ev.image.dataUrl} alt="" sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0', flexShrink: 0 }} />
                  )}
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color, mt: '5px', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{ev.title}</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', mt: '1px' }}>
                      {fmtEventDate(ev)} · {cat.label}{ev.location ? ' · 📍' : ''}{ev.attachment ? ' · 📎' : ''}
                    </Typography>
                    {ev.notes && (
                      <Typography sx={{ fontSize: '12px', color: '#94a3b8', mt: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          <CaseOverviewMap points={mapPoints} onPointClick={id => { const ev = events.find(e => e.id === id); if (ev) openEdit(ev); }} />
        )}
      </Paper>

      <EventEditorDialog
        open={dialogOpen}
        event={editingEvent}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />

      <Backdrop open={exporting} sx={{ zIndex: 1400, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography sx={{ fontSize: '14px' }}>Building your PDF…</Typography>
      </Backdrop>
    </Container>
  );
}

// ─── Route entry ────────────────────────────────────────────────────────────

export default function CaseTimeline() {
  const { timelineId } = useParams();
  return (
    <>
      <Navbar pageTitle="Case Timeline Builder" />
      {timelineId ? <CaseTimelineEditor timelineId={timelineId} /> : <CaseTimelineLanding />}
    </>
  );
}
